/**
 * SEO Configuration Utility
 * Centralized configuration for SEO defaults and site metadata
 */

// Get base URL from environment or use default
export const getBaseUrl = () => {
  // Check if we're in browser
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }
  // Fallback for SSR or build time
  return import.meta.env.VITE_BASE_URL || 'https://fayozltd.com';
};

// Site metadata
export const siteConfig = {
  name: 'Fayoz',
  title: 'Fayoz – Premium Organic Skincare',
  description: 'Discover 100% natural, organic skincare products made to nourish your skin and protect the planet. Shop premium quality, dermatologist-recommended skincare solutions.',
  url: getBaseUrl(),
  image: `${getBaseUrl()}/og-image.jpg`, // Default OG image
  logo: `${getBaseUrl()}/logo.png`,
  twitterHandle: '@fayoz',
  facebookPage: 'https://www.facebook.com/fayoz',
  instagramPage: 'https://www.instagram.com/fayoz',
  locale: 'en_US',
  type: 'website',
  keywords: [
    'organic skincare',
    'natural skincare',
    'premium skincare',
    'Tested and Trusted',
    'cruelty-free skincare',
    'eco-friendly skincare',
    'skincare products',
    'beauty products',
    'natural beauty',
    'organic beauty',
  ].join(', '),
};

// Route-specific SEO configurations
export const routeSeoConfig = {
  home: {
    title: 'Fayoz – Premium Organic Skincare | Natural Beauty Products',
    description: 'Discover 100% natural, organic skincare products made to nourish your skin and protect the planet. Shop premium quality, dermatologist-recommended skincare solutions.',
    keywords: 'organic skincare, natural skincare, premium skincare, beauty products',
    type: 'website',
  },
  shop: {
    title: 'Shop All Products – Fayoz',
    description: 'Browse our complete collection of premium organic skincare products. Find the perfect natural skincare solution for your skin type.',
    keywords: 'shop skincare, organic skincare products, natural beauty products, skincare collection',
    type: 'website',
  },
  product: {
    // Dynamic - will be filled with product data
    type: 'product',
  },
  blog: {
    title: 'Skincare Blog – Fayoz',
    description: 'Expert skincare tips, beauty guides, and insights on natural and organic skincare. Learn how to achieve healthy, radiant skin.',
    keywords: 'skincare blog, beauty tips, skincare advice, organic skincare guide',
    type: 'website',
  },
  blogPost: {
    // Dynamic - will be filled with blog data
    type: 'article',
  },
  about: {
    title: 'About Us – Fayoz',
    description: 'Learn about Fayoz, our mission to provide premium organic skincare products, and our commitment to natural beauty and sustainability.',
    keywords: 'about fayoz, organic skincare brand, natural beauty company',
    type: 'website',
  },
  contact: {
    title: 'Contact Us – Fayoz',
    description: 'Get in touch with Fayoz. Have questions about our products? We\'re here to help you find the perfect skincare solution.',
    keywords: 'contact fayoz, customer support, skincare questions',
    type: 'website',
  },
  cart: {
    title: 'Shopping Cart – Fayoz',
    description: 'Review your selected skincare products and proceed to checkout.',
    keywords: 'shopping cart, skincare cart',
    type: 'website',
    noindex: true, // Don't index cart pages
  },
  checkout: {
    title: 'Checkout – Fayoz',
    description: 'Complete your purchase of premium organic skincare products.',
    keywords: 'checkout, purchase skincare',
    type: 'website',
    noindex: true, // Don't index checkout pages
  },
  reviews: {
    title: 'Customer Reviews – Fayoz',
    description: 'Read authentic customer reviews and testimonials about our premium organic skincare products.',
    keywords: 'skincare reviews, customer testimonials, product reviews',
    type: 'website',
  },
  faq: {
    title: 'Frequently Asked Questions – Fayoz',
    description: 'Find answers to common questions about our organic skincare products, shipping, returns, and more.',
    keywords: 'faq, skincare questions, product information',
    type: 'website',
  },
  privacy: {
    title: 'Privacy Policy – Fayoz',
    description: 'Read our privacy policy to understand how we collect, use, and protect your personal information.',
    keywords: 'privacy policy',
    type: 'website',
  },
  refund: {
    title: 'Refund Policy – Fayoz',
    description: 'Learn about our refund and return policy for skincare products.',
    keywords: 'refund policy, return policy',
    type: 'website',
  },
  terms: {
    title: 'Terms of Service – Fayoz',
    description: 'Read our terms of service for using the Fayoz website and purchasing our products.',
    keywords: 'terms of service',
    type: 'website',
  },
  trackOrder: {
    title: 'Track Your Order – Fayoz',
    description: 'Track the status of your skincare product order.',
    keywords: 'track order, order tracking',
    type: 'website',
    noindex: true,
  },
  paymentCallback: {
    title: 'Payment Verification – Fayoz',
    description: 'Verifying your payment transaction.',
    keywords: 'payment verification, transaction verification',
    type: 'website',
    noindex: true, // Don't index payment callback pages
  },
};

/**
 * Generate canonical URL
 */
export const getCanonicalUrl = (path = '') => {
  const baseUrl = getBaseUrl();
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `${baseUrl}${cleanPath}`;
};

/**
 * Generate page title with site name
 */
export const getPageTitle = (title, includeSiteName = true) => {
  if (!title) return siteConfig.title;
  if (!includeSiteName) return title;
  if (title.includes(siteConfig.name) || title.includes('Fayoz')) {
    return title;
  }
  return `${title} | ${siteConfig.name}`;
};

/**
 * Convert image URL to absolute URL for social media sharing
 * Social media platforms require absolute URLs to fetch images
 */
export const getAbsoluteImageUrl = (imageUrl) => {
  if (!imageUrl || typeof imageUrl !== 'string') {
    return null;
  }
  
  // If already absolute (starts with http:// or https://), return as is
  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
    return imageUrl;
  }
  
  // If relative (starts with /), convert to absolute
  if (imageUrl.startsWith('/')) {
    const baseUrl = getBaseUrl();
    return `${baseUrl}${imageUrl}`;
  }
  
  // If it's a Cloudinary URL without protocol, add https://
  if (imageUrl.includes('cloudinary.com')) {
    return `https://${imageUrl.replace(/^https?:\/\//, '')}`;
  }
  
  // For any other case, try to make it absolute
  const baseUrl = getBaseUrl();
  return `${baseUrl}/${imageUrl.replace(/^\//, '')}`;
};

